/* Coil — buy section behaviors:
 *   1. Keep the add-to-cart total in sync with qty + optional add-on choice.
 *   2. Cross-sell: Yes/No radios. Add-to-cart is gated until one is picked
 *      (aria-disabled, kept focusable + announced — not a silent native
 *      `disabled`). If "Yes", both products are added in one /cart/add.js
 *      request, with HTTP-error handling and a double-submit guard.
 *
 * NOTE: the rack/PSU "cap" gating (rack page allowance, cross-sell greying,
 * cart-page +/- gating, submit pre-flight refusal, auto-clamp on module
 * removal) was removed in favor of a positive post-add prompt — see
 * `coil-rack-prompt.js`. Customers can now buy whatever they want; the
 * prompt nudges them to add a rack & PSU when their module count exceeds
 * what their current racks can hold.
 */
(function () {
  function chosenFor(root) {
    const yes = root.querySelector('[data-coil-add-on-yes]');
    const no = root.querySelector('[data-coil-add-on-no]');
    if (!yes && !no) return null;
    if (yes && yes.checked) return 'yes';
    if (no && no.checked) return 'no';
    return null;
  }

  // Re-trigger an aria-live region so screen readers announce its text again
  // even when the visible text is unchanged.
  function reannounce(el) {
    if (!el) return;
    const msg = el.textContent;
    el.textContent = '';
    requestAnimationFrame(() => {
      el.textContent = msg;
    });
  }

  function showError(el, msg) {
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function init(root) {
    const unitCents = parseInt(root.dataset.unitPrice, 10);
    const currency = root.dataset.currency || 'USD';
    const addOnCents = parseInt(root.dataset.addOnPrice, 10);
    const hasAddOnPrice = Number.isFinite(addOnCents);

    const totalEl = root.querySelector('[data-coil-cart-total]');
    const qtyEl = root.querySelector('input[name="quantity"]');
    const submitBtn =
      root.querySelector('[data-coil-requires-choice]') ||
      root.querySelector('button[type="submit"]');
    const yesRadio = root.querySelector('[data-coil-add-on-yes]');
    const noRadio = root.querySelector('[data-coil-add-on-no]');
    const hintEl = root.querySelector('[data-coil-choice-hint]');
    const formEl = root.querySelector('form');
    const gated = submitBtn && submitBtn.hasAttribute('data-coil-requires-choice');

    if (!qtyEl) return;

    const fmt = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
    });

    function update() {
      const qty = Math.max(parseInt(qtyEl.value, 10) || 1, 1);

      if (totalEl && Number.isFinite(unitCents)) {
        let cents = unitCents * qty;
        if (hasAddOnPrice && chosenFor(root) === 'yes') {
          cents += addOnCents;
        }
        totalEl.textContent = fmt.format(cents / 100);
      }

      // Cross-sell gate: ATC stays aria-disabled (focusable, announced) until
      // Yes or No is picked. The visible hint provides a non-color cue.
      if (gated) {
        const chosen = chosenFor(root) !== null;
        submitBtn.setAttribute('aria-disabled', chosen ? 'false' : 'true');
        if (hintEl) hintEl.hidden = chosen;
      }
    }

    qtyEl.addEventListener('input', update);
    qtyEl.addEventListener('change', update);

    // Split's qty +/- buttons mutate input.value without firing events.
    root.querySelectorAll('.qty-button').forEach((btn) => {
      btn.addEventListener('click', () => requestAnimationFrame(update));
    });

    if (yesRadio) yesRadio.addEventListener('change', update);
    if (noRadio) noRadio.addEventListener('change', update);

    // Gate enforcement: aria-disabled doesn't block activation on its own, so
    // intercept a submit with no choice. Runs before the cross-sell handler
    // (attached later) and stops it via stopImmediatePropagation.
    if (gated && formEl) {
      formEl.addEventListener('submit', (e) => {
        if (chosenFor(root) !== null) return; // a choice exists → allow through
        e.preventDefault();
        e.stopImmediatePropagation();
        const firstRadio = yesRadio || noRadio;
        if (firstRadio) firstRadio.focus();
        reannounce(hintEl);
      });
    }

    update();
  }

  // Cross-sell "Yes" submit handler: POST both the current product and the
  // add-on product to /cart/add.js in one request, then go to /cart. For
  // "No" (or no cross-sell at all), the form's normal submit handles it.
  function attachCrossSellSubmit(root) {
    const form = root.querySelector('form');
    const qtyEl = root.querySelector('input[name="quantity"]');
    if (!form || !qtyEl) return;

    const addOnId = parseInt(root.dataset.addOnId, 10);
    if (!Number.isFinite(addOnId)) return;

    const errEl = root.querySelector('[data-coil-buy-error]');
    const submitBtn =
      root.querySelector('[data-coil-requires-choice]') ||
      root.querySelector('button[type="submit"]');
    let submitting = false;

    form.addEventListener('submit', (e) => {
      if (chosenFor(root) !== 'yes') return; // No / nothing picked → normal submit
      e.preventDefault();
      if (submitting) return; // double-submit guard
      submitting = true;
      if (submitBtn) submitBtn.setAttribute('aria-disabled', 'true');
      if (errEl) {
        errEl.hidden = true;
        errEl.textContent = '';
      }

      const formData = new FormData(form);
      const items = [
        {
          id: parseInt(formData.get('id'), 10),
          quantity: Math.max(parseInt(formData.get('quantity'), 10) || 1, 1),
        },
        { id: addOnId, quantity: 1 },
      ];

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      })
        // fetch() does NOT reject on HTTP 4xx/5xx — read r.ok ourselves and
        // surface Shopify's {status, description, message} error body.
        .then((r) => r.json().then((data) => ({ ok: r.ok, data })))
        .then(({ ok, data }) => {
          if (!ok) {
            const msg =
              (data && (data.description || data.message)) ||
              "Sorry — we couldn't add these to your cart. Please try again.";
            showError(errEl, msg);
            submitting = false;
            if (submitBtn) submitBtn.setAttribute('aria-disabled', 'false');
            return;
          }
          window.location.href = '/cart';
        })
        .catch(() => {
          // Network error: fall back to a normal single-item form submit so the
          // customer still gets the main product into their cart.
          submitting = false;
          form.submit();
        });
    });
  }

  document.querySelectorAll('[data-coil-buy]').forEach(init);
  document.querySelectorAll('[data-coil-buy]').forEach(attachCrossSellSubmit);
})();
